// MACHINERY V2 (Stroje) — profile action set, dialog state + handlers (M2.4).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §10 (permission matrix) + data
// package §1 "Actions (+ gates)". Reference impl: fleet/useFleetHandlers.ts.
//
// >>> THE ACTION SET IS THE FLEET'S. THE GATES ARE THE FLEET'S. <<<
// `buildFleetActions(machine, canWrite, handlers)` decides which entries a given
// user sees on a given machine — here exactly as on a card or a row. The gate
// keeps exactly one implementation, and `canWrite` is the same
// `usePermission("Machinery")` the live profile's PermissionGuard checks, so a
// Worker never sees a write-level entry.
//
// THE COLLAPSE IS NOT UNIFORM ACROSS SURFACES. FleetCardMenu.tsx cites THIS
// file as the authority for the profile half, so keep these four in step with
// it — a decommissioned machine yields:
//   FLEET   · Write user : Profil · QR · Obnoviť
//   FLEET   · Worker     : Profil alone
//   PROFILE · Write user : QR only — 2. below promotes Obnoviť into the banner
//                          and filters the self-referential Profil, and
//                          reportProblem is suppressed when decommissioned
//   PROFILE · Worker     : EMPTY — FleetCardMenu renders no kebab at all
// The fleet's list is therefore never empty; only the profile's can be.
//
// WHAT DIFFERS FROM THE FLEET, AND WHY:
//  1. REFRESH TARGET. Every profile mutation refetches THIS machine
//     (`getMachine(id)`), not the fleet lists. Reusing useFleetHandlers verbatim
//     would refetch a grid nobody is looking at and leave the hero rendering
//     pre-edit values — the decommission banner would never appear. Same
//     handlers, surface-correct refresh.
//  2. FOUR ENTRIES ARE PROMOTED OUT OF THE KEBAB (artboard 0504): Vytvoriť
//     servisnú správu + Vytvoriť žiadosť become the two hero buttons, Obnoviť
//     lives in the decommission banner (data package §1: "rendered only inside
//     the banner"), and Profil is self-referential on the profile. They are
//     FILTERED from the menu, never re-gated: the hero buttons render only when
//     buildFleetActions put their entry in the set, so the gate still has
//     exactly one implementation.
//  3. REPORT PROBLEM IS ADDED. It is a profile action in data package §1
//     (Read-level, hidden when decommissioned) that no fleet surface carries,
//     so `buildFleetActions` has no entry for it. Dropping it would be a §1
//     action-parity regression, so it is appended here — additive, never a
//     modification of the shared builder.
//
// Toasts are the sanctioned v2 primitive: handlers dispatch setSuccess/setError
// and MachineryToastHost (mounted by MachineProfileV2) renders them. Restore
// reuses the EXISTING confirmation dialog, exactly as the fleet does.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import usePermission from "../../../../shared/hooks/usePermission";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import { Modules } from "../../../../core/models/PermissionModels";
import { Machine } from "../../models/models";
import {
  createMachine,
  getMachine,
  restoreMachine,
  setError,
  setSuccess,
} from "../../customerMachinerySlice";
import { FleetMenuAction } from "../fleet/FleetCardMenu";
import { buildFleetActions } from "../fleet/fleetActions";
import { buildDuplicatePayload } from "../fleet/fleetDuplicate";
import { ProfileDialogsProps } from "./ProfileDialogs";

/** Entries the profile renders as page chrome instead of menu rows (see 2. and
 *  3. in the header). Filtered, NOT re-gated. */
const PROMOTED_OUT_OF_KEBAB = ["profile", "sr", "rfs", "restore"];

export interface ProfileActions {
  /** Kebab entries for the hero's action stack. */
  actions: FleetMenuAction[];
  /** Null when the gate/state removed the entry — the button then does not render. */
  onCreateServiceReport: (() => void) | null;
  onCreateServiceRequest: (() => void) | null;
  /** Banner-only (data package §1). Null when the user cannot restore. */
  onRestore: (() => void) | null;
  dialogProps: Omit<ProfileDialogsProps, "machine">;
}

export const useProfileActions = (machine: Machine): ProfileActions => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const { canWrite } = usePermission(Modules.Machinery);

  const [qrOpen, setQrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [decommissionOpen, setDecommissionOpen] = useState(false);
  const [reportProblemOpen, setReportProblemOpen] = useState(false);

  const machineId = machine.id;

  /** The one refresh every profile mutation ends with. */
  const refresh = useCallback(() => {
    dispatch(getMachine(machineId));
  }, [dispatch, machineId]);

  const handleDuplicate = useCallback(
    async (m: Machine) => {
      // The v2 payload carries catalogModelId AND locationId (fleetDuplicate.ts
      // — the §9 D4 fix). Shared with the fleet so it cannot regress on one
      // surface only.
      const payload = buildDuplicatePayload(m);
      if (!payload) {
        dispatch(setError(translate("myMachinery:v2.fleet.duplicateMissingFields")));
        return;
      }
      // REVIEW (M2.4). The dispatch was NOT unwrapped, so a REJECTED create
      // resolved normally and the success toast fired anyway — stacked under
      // the error toast the slice had just raised. `handleRestore` ten lines
      // below is the pattern: unwrap inside a try, toast ONLY on the success
      // path, swallow in catch (the slice already stored the error and
      // MachineryToastHost renders it).
      try {
        await dispatch(createMachine(payload)).unwrap();
        // No refresh: the copy is a DIFFERENT machine and this page shows the
        // original, which did not change. The toast is the only feedback there
        // is — on the fleet the new card appearing is the feedback.
        dispatch(setSuccess(translate("myMachinery:v2.profile.duplicated")));
      } catch {
        // Error surfaced by the slice -> MachineryToastHost.
      }
    },
    [dispatch, translate]
  );

  const handleRestore = useCallback(() => {
    // The EXISTING confirm dialog, reused — §9 D4 carries the action, not a
    // redesign of its dialog.
    confirm({
      title: translate("myMachinery:restoreMachine"),
      text: translate("myMachinery:restoreConfirm"),
      confirmButton: translate("myMachinery:restore"),
      // `common.json` has NO top-level `cancel` key — tCommon("cancel")
      // rendered the literal string "cancel" in BOTH locales. The real key is
      // buttons.cancel (en "Cancel" / sk "Zrušiť").
      closeButton: tCommon("buttons.cancel"),
      severity: "neutral",
      // ConfirmationProvider neither awaits nor catches this callback, so an
      // unwrapped rejection would be an unhandled promise rejection AND would
      // skip the refresh (fleet review F6, same fix).
      onConfirm: async () => {
        try {
          await dispatch(restoreMachine(machineId)).unwrap();
          dispatch(setSuccess(translate("myMachinery:restored")));
        } catch {
          // Error surfaced by the slice -> MachineryToastHost.
        }
        refresh();
      },
    });
  }, [confirm, dispatch, machineId, refresh, translate, tCommon]);

  const fullActions = useMemo(
    () =>
      buildFleetActions(machine, canWrite, {
        // Self-referential here; filtered out of the menu below. Kept as a
        // no-op so the handler map stays complete and type-checked.
        onProfile: () => undefined,
        onQrCode: () => setQrOpen(true),
        // Same target the fleet uses; M2.6 repoints both to the v2 SR pages.
        onCreateServiceReport: (m) => navigate(`/machinery/${m.id}/service-reports/new`),
        // The live profile carries a returnTo (data package §1); it points at
        // the v2 profile so the round trip stays on the v2 surface.
        onCreateServiceRequest: (m) =>
          navigate(
            `/service-requests/new?machine=${m.id}&returnTo=/machinery-v2/${m.id}`
          ),
        onEditDetails: () => setEditOpen(true),
        onDuplicate: handleDuplicate,
        onAssignGroup: () => setAssignGroupOpen(true),
        onDecommission: () => setDecommissionOpen(true),
        onRestore: handleRestore,
      }),
    [machine, canWrite, navigate, handleDuplicate, handleRestore]
  );

  const findAction = useCallback(
    (id: string) => fullActions.find((a) => a.id === id) || null,
    [fullActions]
  );

  const actions = useMemo(() => {
    const kebab = fullActions.filter(
      (a) => PROMOTED_OUT_OF_KEBAB.indexOf(a.id) < 0
    );
    // Read-level, and hidden on a decommissioned machine — the live profile's
    // exact gate. Suppressing it here is what leaves the PROFILE's
    // decommissioned kebab at QR alone; data package §1's "QR is the sole
    // surviving action" is about THIS surface only. The fleet's kebab still
    // carries Profil, plus Obnoviť for a Write user.
    if (!machine.isDecommissioned) {
      kebab.push({
        id: "reportProblem",
        labelKey: "myMachinery:reportProblem.button",
        onSelect: () => setReportProblemOpen(true),
      });
    }
    return kebab;
  }, [fullActions, machine.isDecommissioned]);

  const srAction = findAction("sr");
  const rfsAction = findAction("rfs");
  const restoreAction = findAction("restore");

  const dialogProps: Omit<ProfileDialogsProps, "machine"> = {
    qrOpen,
    onQrClose: () => setQrOpen(false),
    editOpen,
    onEditClose: () => setEditOpen(false),
    onEditSaved: () => {
      setEditOpen(false);
      refresh();
    },
    assignGroupOpen,
    onAssignGroupClose: () => setAssignGroupOpen(false),
    onAssigned: () => {
      setAssignGroupOpen(false);
      refresh();
    },
    decommissionOpen,
    onDecommissionClose: () => setDecommissionOpen(false),
    onDecommissioned: () => {
      setDecommissionOpen(false);
      refresh();
    },
    reportProblemOpen,
    onReportProblemClose: () => setReportProblemOpen(false),
    onReportProblemSuccess: (srNumber: string) => {
      dispatch(
        setSuccess(translate("myMachinery:reportProblem.successDetail", { number: srNumber }))
      );
      refresh();
    },
  };

  return {
    actions,
    onCreateServiceReport: srAction ? srAction.onSelect : null,
    onCreateServiceRequest: rfsAction ? rfsAction.onSelect : null,
    onRestore: restoreAction ? restoreAction.onSelect : null,
    dialogProps,
  };
};

export default useProfileActions;
